export type SurveyColorSchemeSetting = "Light" | "Dark" | "LightAndDark";
export type ResolvedColorScheme = "Light" | "Dark";

export function getSurveyColorScheme(
    supported: SurveyColorSchemeSetting | null | undefined,
    prefersDark: boolean
): ResolvedColorScheme {
    // Unset means Light, NOT LightAndDark as at the project level: an existing
    // survey has no setting and must not flip to dark on a dark device.
    const setting = supported || "Light";
    if (setting === "LightAndDark") {
        // Follows the device, not the project, for accessibility
        return prefersDark ? "Dark" : "Light";
    }
    return setting === "Dark" ? "Dark" : "Light";
}

function prefersDark(): boolean {
    return !!window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
}

// One active stamp per element: when a new flow claims an element the previous
// stamp's media listener is retired, so a finished survey cannot keep re-applying
// its scheme under the flow that took over the page.
const activeStamps = new WeakMap<HTMLElement, () => void>();

// Stamp the survey's resolved scheme on an element: the modal for in-app surveys,
// <body> on standalone pages that own it. Returns a cleanup for when the survey ends.
export function stampSurveyColorScheme(element: HTMLElement, supported: SurveyColorSchemeSetting | null | undefined): () => void {
    activeStamps.get(element)?.();
    const apply = () => {
        const scheme = getSurveyColorScheme(supported, prefersDark());
        element.classList.toggle("survey-scheme-dark", scheme === "Dark");
        element.classList.toggle("survey-scheme-light", scheme === "Light");
    };
    apply();

    let query: MediaQueryList | null = null;
    if ((supported || "Light") === "LightAndDark" && window.matchMedia) {
        query = window.matchMedia("(prefers-color-scheme: dark)");
        query.addEventListener("change", apply);
    }
    const cleanup = () => {
        query?.removeEventListener("change", apply);
        element.classList.remove("survey-scheme-dark", "survey-scheme-light");
        if (activeStamps.get(element) === cleanup) {
            activeStamps.delete(element);
        }
    };
    activeStamps.set(element, cleanup);
    return cleanup;
}

// The nearest survey scheme wins, never the app's color-scheme-*: a survey stays light inside a dark app unless it opts in.
export function rendersDark(element: Element): boolean {
    return !!element.closest(".survey-scheme-dark, .survey-scheme-light")?.classList.contains("survey-scheme-dark");
}

// Only the builder's preview switch flips the scheme mid-step; live, the next step renders in it.
export function followsPreviewSwitch(stepManager: { surveyManager: { getMode(): string } }): boolean {
    return stepManager.surveyManager.getMode() !== "Survey";
}

// An iframe inherits neither our CSS variables nor the scheme class, so the frame root gets its own.
export function stampSchemeOnFrame(hostElement: Element, frameDocument: Document): void {
    const dark = rendersDark(hostElement);
    frameDocument.documentElement.classList.toggle("survey-scheme-dark", dark);
    frameDocument.documentElement.classList.toggle("survey-scheme-light", !dark);
}
